import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  keyframes,
  List,
  ListItem,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { fetchVectorStatus, readCachedVectorState } from "./vectorStatusCache";

const REFRESH_MS = 30000;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 var(--dot-glow); }
  70% { box-shadow: 0 0 0 8px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const STATES = {
  synced: {
    color: "green.400",
    glow: "rgba(72, 187, 120, 0.6)",
    label: "Synced",
    text: "Every enabled product is in the vector store.",
  },
  partial: {
    color: "yellow.400",
    glow: "rgba(236, 201, 75, 0.6)",
    label: "Out of sync",
    text: "The vector store does not match the store's products.",
  },
  empty: {
    color: "red.500",
    glow: "rgba(229, 62, 62, 0.6)",
    label: "No data",
    text: "The vector store has no products for this store.",
  },
  unknown: {
    color: "gray.400",
    glow: "rgba(160, 174, 192, 0.6)",
    label: "Unknown",
    text: "The sync state could not be checked.",
  },
};

export const StatusDot = ({ state, size = "10px" }) => {
  const config = STATES[state] || STATES.unknown;

  return (
    <Box
      as="span"
      display="inline-block"
      flexShrink={0}
      w={size}
      h={size}
      borderRadius="full"
      bg={config.color}
      sx={{ "--dot-glow": config.glow }}
      animation={state ? `${pulse} 2s infinite` : undefined}
    />
  );
};

const ProductList = ({ title, items, total }) =>
  total > 0 && (
    <Box mt={3}>
      <Text fontSize="sm" fontWeight="semibold" mb={1}>
        {title} ({total})
      </Text>
      <List spacing={0.5} fontSize="sm" maxH="180px" overflowY="auto">
        {items.map((item) => (
          <ListItem key={item.id}>
            <Text as="span" fontFamily="mono" color="gray.500" mr={2}>
              #{item.id}
            </Text>
            {item.name || "—"}
          </ListItem>
        ))}
        {total > items.length && (
          <ListItem color="gray.500">…and {total - items.length} more</ListItem>
        )}
      </List>
    </Box>
  );

/**
 * Small dot for a table row: paints the last known state from localStorage at
 * once, then asks the server (its cached answer) in the background.
 */
export const StoreVectorDot = ({ storeId }) => {
  const [state, setState] = useState(
    () => readCachedVectorState(storeId)?.state || null
  );

  useEffect(() => {
    let cancelled = false;

    fetchVectorStatus(storeId)
      .then((data) => !cancelled && setState(data.state))
      .catch(() => !cancelled && setState((current) => current || "unknown"));

    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const label = state
    ? `Vector store: ${(STATES[state] || STATES.unknown).label}`
    : "Vector store: checking…";

  return (
    <Tooltip label={label} openDelay={200}>
      <Box as="span" display="inline-flex" opacity={state ? 1 : 0.35}>
        <StatusDot state={state || "unknown"} size="8px" />
      </Box>
    </Tooltip>
  );
};

/**
 * Live sync state of the store's bot vector store (Weaviate). Mounted only
 * while the Store data tab is open: it starts from the cached state, asks for
 * a fresh scan, then polls every REFRESH_MS.
 */
const VectorStoreStatus = ({ storeId, onState }) => {
  const [status, setStatus] = useState(() => {
    const cached = readCachedVectorState(storeId);
    return cached ? { ...cached, fromCache: true } : null;
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const onStateRef = useRef(onState);
  onStateRef.current = onState;

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const data = await fetchVectorStatus(storeId, { fresh: true });
      setStatus(data);
      onStateRef.current?.(data.state);
    } catch (error) {
      setStatus({
        state: "unknown",
        error: error?.response?.data?.message || "Request failed.",
      });
      onStateRef.current?.("unknown");
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const config = STATES[status?.state] || STATES.unknown;
  const hasDetails =
    status && (status.missingCount > 0 || status.staleCount > 0);

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={6}>
      <Flex align="center" gap={3} wrap="wrap">
        <StatusDot state={status?.state} size="12px" />
        <Box>
          <Text fontWeight="semibold">
            Vector store: {status ? config.label : "checking…"}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {status?.error ||
              (status && !status.fromCache
                ? status.state === "synced" && status.expected === 0
                  ? "No enabled products yet, so there is nothing to index."
                  : config.text
                : "Comparing products…")}
          </Text>
        </Box>
        <Box flex="1" />
        {status && !status.error && !status.fromCache && (
          <HStack spacing={4} fontSize="sm" color="gray.600">
            <Tooltip label="Enabled products in the store database">
              <Text>
                Store <b>{status.expected}</b>
              </Text>
            </Tooltip>
            <Tooltip label="Distinct products found in Weaviate">
              <Text>
                Indexed <b>{status.indexed}</b>
              </Text>
            </Tooltip>
            <Tooltip label="Weaviate objects, one per product translation">
              <Text>
                Objects <b>{status.objects}</b>
              </Text>
            </Tooltip>
          </HStack>
        )}
        <Button
          size="xs"
          variant="ghost"
          leftIcon={<RepeatIcon />}
          onClick={load}
          isLoading={loading}
        >
          Check
        </Button>
      </Flex>

      {status && (
        <Flex mt={2} fontSize="xs" color="gray.500" gap={3} wrap="wrap">
          {status.indexName && (
            <Text>
              Class{" "}
              <Text as="span" fontFamily="mono">
                {status.indexName}
              </Text>
            </Text>
          )}
          {status.checkedAt && (
            <Text>
              Checked {new Date(status.checkedAt).toLocaleTimeString()} ·
              refreshes every 30 s
            </Text>
          )}
          {status.truncated && <Text>Scan stopped at 50,000 objects.</Text>}
          {hasDetails && (
            <Button
              size="xs"
              variant="link"
              colorScheme="blue"
              onClick={() => setOpen((value) => !value)}
            >
              {open ? "Hide details" : "Show details"}
            </Button>
          )}
        </Flex>
      )}

      {hasDetails && (
        <Collapse in={open} animateOpacity>
          <ProductList
            title="Missing from the vector store"
            items={status.missing}
            total={status.missingCount}
          />
          <ProductList
            title="In the vector store but disabled or deleted"
            items={status.stale}
            total={status.staleCount}
          />
        </Collapse>
      )}
    </Box>
  );
};

export default VectorStoreStatus;
